import type { AppAction } from '@/store/actions'
import type { AppState } from '@/store/reducer'
import {
  createTrip,
  updateTrip,
  deleteTrip,
  createItineraryDay,
  updateItineraryDay,
  deleteItineraryDay,
  createActivity,
  updateActivity,
  deleteActivity,
  updateActivitiesSortOrder,
  createQuote,
  updateQuote,
  createQuoteItem,
  updateQuoteItem,
  deleteQuoteItem,
  createPaymentMilestone,
  updatePaymentMilestone,
  deletePaymentMilestone,
  createParticipant,
  updateParticipant,
  deleteParticipant,
  createTask,
  updateTask,
  deleteTask,
  createNote,
  updateNote,
  deleteNote,
  createPartner,
  updatePartner,
  deletePartner,
  assignPartnerToTrip,
  removePartnerFromTrip,
  createExpenseActual,
  updateExpenseActual,
  deleteExpenseActual,
  createExpenseEstimate,
  updateExpenseEstimate,
  deleteExpenseEstimate,
} from './data'

/**
 * Deep-equal check for primitive values and arrays (single level).
 */
function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => v === b[i])
  }
  return false
}

/**
 * Find a newly added entity by matching all payload fields against items in the array.
 * The reducer creates entities by spreading payload + generated fields (id, created_at, etc.),
 * so every payload field will be present on the entity with the same value.
 * Searches in reverse order since the new entity is likely near the end.
 */
function findByPayload<T>(
  items: T[],
  payload: Record<string, unknown>,
): T | undefined {
  const keys = Object.keys(payload)
  for (let i = items.length - 1; i >= 0; i--) {
    const item = items[i] as Record<string, unknown>
    if (keys.every((k) => valuesEqual(item[k], payload[k]))) {
      return items[i]
    }
  }
  return undefined
}

/**
 * Maps reducer actions to backend CRUD calls (Rust API via the /api/data BFF).
 * Called after the optimistic local state update.
 * `newState` is the state AFTER the reducer ran, so we can read generated IDs.
 */
export async function syncToBackend(action: AppAction, newState: AppState): Promise<void> {
  switch (action.type) {
    // ==================== TRIPS ====================

    case 'ADD_CAMP': {
      const camp = findByPayload(newState.camps, action.payload)
      if (camp) {
        await createTrip(camp)
      }
      break
    }

    case 'UPDATE_CAMP': {
      const { id, ...updates } = action.payload
      await updateTrip(id, { ...updates, updated_at: new Date().toISOString() })
      break
    }

    case 'DELETE_CAMP': {
      await deleteTrip(action.payload.id)
      break
    }

    case 'CHANGE_CAMP_STATUS': {
      const { id, status } = action.payload
      await updateTrip(id, { status, updated_at: new Date().toISOString() })
      break
    }

    case 'DUPLICATE_TRIP': {
      // The reducer creates a full copy with new IDs.
      // We need to insert the new trip and all its related data.
      // Find the duplicated trip: it references the original's name with "コピー" suffix.
      const originalTrip = newState.camps.find((t) => t.id === action.payload.id)
      if (!originalTrip) break
      const expectedName = `${originalTrip.name} (コピー)`
      // Among all matching candidates, pick the one with the latest created_at
      const duplicatedCamp = newState.camps
        .filter((t) => t.id !== action.payload.id && t.name === expectedName && t.status === 'inquiry')
        .sort((a, b) => b.created_at.localeCompare(a.created_at))[0]
      if (!duplicatedCamp) break

      const campData = newState.campData[duplicatedCamp.id]
      if (!campData) break

      // Insert trip
      await createTrip(duplicatedCamp)

      // Insert related data in parallel
      await Promise.all([
        ...campData.itineraryDays.map((d) => createItineraryDay(d)),
        ...campData.activities.map((a) => createActivity(a)),
        ...campData.quotes.map((q) => createQuote(q)),
        ...campData.quoteItems.map((qi) => createQuoteItem(qi)),
        ...campData.paymentMilestones.map((m) => createPaymentMilestone(m)),
        ...campData.participants.map((p) => createParticipant(p)),
        ...campData.tasks.map((t) => createTask(t)),
        ...campData.notes.map((n) => createNote(n)),
        ...campData.campPartners.map((tp) => assignPartnerToTrip(tp)),
        ...campData.expenseActuals.map((ea) => createExpenseActual(ea)),
        ...campData.expenseEstimates.map((ee) => createExpenseEstimate(ee)),
      ])
      break
    }

    // ==================== ITINERARY ====================

    case 'ADD_ITINERARY_DAY': {
      const campId = action.payload.camp_id
      const data = newState.campData[campId]
      const day = data && findByPayload(data.itineraryDays, action.payload)
      if (day) {
        await createItineraryDay(day)
      }
      break
    }

    case 'UPDATE_ITINERARY_DAY': {
      const { id, ...updates } = action.payload
      await updateItineraryDay(id, updates)
      break
    }

    case 'DELETE_ITINERARY_DAY': {
      await deleteItineraryDay(action.payload.id)
      break
    }

    case 'ADD_ACTIVITY': {
      // Find the trip that contains this activity's day
      const dayId = action.payload.itinerary_day_id
      const campId = Object.keys(newState.campData).find((tid) =>
        newState.campData[tid].itineraryDays.some((d) => d.id === dayId),
      )
      if (!campId) break
      const data = newState.campData[campId]
      const activity = findByPayload(data.activities, action.payload)
      if (activity) {
        await createActivity(activity)
      }
      break
    }

    case 'UPDATE_ACTIVITY': {
      const { id, campId: _campId, ...updates } = action.payload
      await updateActivity(id, updates)
      break
    }

    case 'DELETE_ACTIVITY': {
      await deleteActivity(action.payload.id)
      break
    }

    case 'REORDER_ACTIVITIES': {
      const { campId, itineraryDayId, activityIds } = action.payload
      const data = newState.campData[campId]
      if (!data) break
      const updates = data.activities
        .filter((a) => a.itinerary_day_id === itineraryDayId)
        .map((a) => ({
          id: a.id,
          sort_order: activityIds.indexOf(a.id) + 1,
        }))
        .filter((u) => u.sort_order > 0)
      await updateActivitiesSortOrder(updates)
      break
    }

    // ==================== QUOTES ====================

    case 'ADD_QUOTE': {
      const campId = action.payload.camp_id
      const data = newState.campData[campId]
      const quote = data && findByPayload(data.quotes, action.payload)
      if (quote) {
        await createQuote(quote)
      }
      break
    }

    case 'UPDATE_QUOTE': {
      const { id, campId: _campId, ...updates } = action.payload
      await updateQuote(id, updates)
      break
    }

    case 'ADD_QUOTE_ITEM': {
      const { campId, ...itemPayload } = action.payload
      const data = newState.campData[campId]
      const item = data && findByPayload(data.quoteItems, itemPayload)
      if (item) {
        await createQuoteItem(item)
      }
      break
    }

    case 'UPDATE_QUOTE_ITEM': {
      const { id, campId: _campId, ...updates } = action.payload
      await updateQuoteItem(id, updates)
      break
    }

    case 'DELETE_QUOTE_ITEM': {
      await deleteQuoteItem(action.payload.id)
      break
    }

    case 'RECALCULATE_QUOTE_TOTAL': {
      const { quoteId, campId } = action.payload
      const data = newState.campData[campId]
      const quote = data?.quotes.find((q) => q.id === quoteId)
      if (quote) {
        await updateQuote(quoteId, { total: quote.total })
      }
      break
    }

    // ==================== PAYMENTS ====================

    case 'ADD_PAYMENT_MILESTONE': {
      const campId = action.payload.camp_id
      const data = newState.campData[campId]
      const milestone = data && findByPayload(data.paymentMilestones, action.payload)
      if (milestone) {
        await createPaymentMilestone(milestone)
      }
      break
    }

    case 'UPDATE_PAYMENT_MILESTONE': {
      const { id, campId: _campId, ...updates } = action.payload
      await updatePaymentMilestone(id, updates)
      break
    }

    case 'MARK_PAYMENT_PAID': {
      const { id } = action.payload
      await updatePaymentMilestone(id, { status: 'paid', paid_at: new Date().toISOString() })
      break
    }

    case 'DELETE_PAYMENT_MILESTONE': {
      const { id } = action.payload
      await deletePaymentMilestone(id)
      break
    }

    // ==================== PARTICIPANTS ====================

    case 'ADD_PARTICIPANT': {
      const campId = action.payload.camp_id
      const data = newState.campData[campId]
      const participant = data && findByPayload(data.participants, action.payload)
      if (participant) {
        await createParticipant(participant)
      }
      break
    }

    case 'UPDATE_PARTICIPANT': {
      const { id, campId: _campId, ...updates } = action.payload
      await updateParticipant(id, updates)
      break
    }

    case 'DELETE_PARTICIPANT': {
      await deleteParticipant(action.payload.id)
      break
    }

    case 'UPDATE_PARTICIPANT_STATUS': {
      const { id, status } = action.payload
      await updateParticipant(id, { status })
      break
    }

    // ==================== TASKS ====================

    case 'ADD_TASK': {
      const campId = action.payload.camp_id
      const data = newState.campData[campId]
      const task = data && findByPayload(data.tasks, action.payload)
      if (task) {
        await createTask(task)
      }
      break
    }

    case 'UPDATE_TASK': {
      const { id, campId: _campId, ...updates } = action.payload
      await updateTask(id, updates)
      break
    }

    case 'DELETE_TASK': {
      await deleteTask(action.payload.id)
      break
    }

    case 'UPDATE_TASK_STATUS': {
      const { id, status } = action.payload
      await updateTask(id, { status })
      break
    }

    // ==================== NOTES ====================

    case 'ADD_NOTE': {
      const campId = action.payload.camp_id
      const data = newState.campData[campId]
      const note = data && findByPayload(data.notes, action.payload)
      if (note) {
        await createNote(note)
      }
      break
    }

    case 'UPDATE_NOTE': {
      const { id, campId: _campId, ...updates } = action.payload
      await updateNote(id, updates)
      break
    }

    case 'DELETE_NOTE': {
      await deleteNote(action.payload.id)
      break
    }

    // ==================== EXPENSE ACTUALS ====================

    case 'ADD_EXPENSE_ACTUAL': {
      const campId = action.payload.camp_id
      const data = newState.campData[campId]
      const expense = data && findByPayload(data.expenseActuals, action.payload)
      if (expense) {
        await createExpenseActual(expense)
      }
      break
    }

    case 'UPDATE_EXPENSE_ACTUAL': {
      const { id, campId: _campId, ...updates } = action.payload
      await updateExpenseActual(id, updates)
      break
    }

    case 'DELETE_EXPENSE_ACTUAL': {
      await deleteExpenseActual(action.payload.id)
      break
    }

    // ==================== EXPENSE ESTIMATES ====================

    case 'ADD_EXPENSE_ESTIMATE': {
      const campId = action.payload.camp_id
      const data = newState.campData[campId]
      const estimate = data && findByPayload(data.expenseEstimates, action.payload)
      if (estimate) {
        await createExpenseEstimate(estimate)
      }
      break
    }

    case 'UPDATE_EXPENSE_ESTIMATE': {
      const { id, campId: _campId, ...updates } = action.payload
      await updateExpenseEstimate(id, updates)
      break
    }

    case 'DELETE_EXPENSE_ESTIMATE': {
      await deleteExpenseEstimate(action.payload.id)
      break
    }

    // ==================== PARTNERS ====================

    case 'ADD_PARTNER': {
      const partner = findByPayload(newState.partners, action.payload)
      if (partner) {
        await createPartner(partner)
      }
      break
    }

    case 'UPDATE_PARTNER': {
      const { id, ...updates } = action.payload
      await updatePartner(id, updates)
      break
    }

    case 'DELETE_PARTNER': {
      await deletePartner(action.payload.id)
      break
    }

    case 'ASSIGN_PARTNER_TO_TRIP': {
      const campId = action.payload.camp_id
      const data = newState.campData[campId]
      const campPartner = data && findByPayload(data.campPartners, action.payload)
      if (campPartner) {
        await assignPartnerToTrip(campPartner)
      }
      break
    }

    case 'REMOVE_PARTNER_FROM_TRIP': {
      await removePartnerFromTrip(action.payload.id)
      break
    }

    default:
      break
  }
}
